import i18n from '@/i18n';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { api } from '@/services/api';
import { useTheme } from '@/hooks/useTheme';
import UeexRedesign from './UeexRedesign';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer, CartesianGrid, ComposedChart,
} from 'recharts';

// =============================================================================
// Types
// =============================================================================
type GoodsKey = 'base' | 'block' | 'peak' | 'offpeak' | 'holidaypeak';

interface TimeSeriesPoint {
  date: string;
  base_price?: number | null;
  block_price?: number | null;
  base_volume_mwh?: number | null;
  block_volume_mwh?: number | null;
  peak_price?: number | null;
  offpeak_price?: number | null;
  [key: string]: any;
}

interface ElectricityTimeseriesResponse {
  windowDays: number;
  rows: number;
  stats: Record<string, { goods: string; totalMwh: number; totalUah: number; positions: number; wavgPrice: number | null }>;
  points: TimeSeriesPoint[];
}

interface SolarGoPoint {
  date: string;
  sellPositions: number;
  closedWithBuyer: number;
  offeredMwh: number | null;
  realizedMwh: number | null;
  weightedAvgPrice: number | null;
  avgAskPrice: number | null;
  minAskPrice: number | null;
  maxAskPrice: number | null;
}

interface SolarGoResponse { points: SolarGoPoint[]; windowDays: number; }

interface TopSeller {
  member: string;
  memberCode: string;
  closedDeals: number;
  soldMwh: number | null;
  revenueUah: number | null;
  wavgPrice: number | null;
}

interface Auction {
  aucNum: string;
  aucDate: string;
  aucTypeName: string;
  aucState: string;
  positionsCount: number;
  closedCount: number;
  totalMwh: number | null;
  totalUah: number | null;
  wavgPrice: number | null;
  goods: string[];
}

// =============================================================================
// Helpers
// =============================================================================
const fmtNum = (v: any, digits = 0): string => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString('uk-UA', { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

const fmtDate = (d: string): string => {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return `${day}.${m}.${y}`;
};

type SectionKey =
  | 'electric_power'          // Електроенергія (основна — chart + bilateral)
  | 'bilateral_contracts'     // Двосторонні договори (table)
  | 'solar_go'                // Solar GO (Сонячні ГП)
  | 'renewable_go'            // Усі ВДЕ ГП (порівняння Solar/Wind/Hydro/Bio)
  | 'auctions';               // Список усіх аукціонів

const SECTION_DEFS: { key: SectionKey; label_key: string; icon: string }[] = [
  { key: 'electric_power',      label_key: 'ueex_section_electric_power',      icon: 'ri-flashlight-line' },
  { key: 'bilateral_contracts', label_key: 'ueex_section_bilateral_contracts', icon: 'ri-handshake-line' },
  { key: 'solar_go',            label_key: 'ueex_section_solar_go',            icon: 'ri-sun-line' },
  { key: 'renewable_go',        label_key: 'ueex_section_renewable_go',        icon: 'ri-leaf-line' },
  { key: 'auctions',            label_key: 'ueex_section_auctions',            icon: 'ri-list-check' },
];

// =============================================================================
// Page
// =============================================================================
/**
 * Dual mode (handoff section 6): stand with VITE_REDESIGN shows the redesign,
 * prod keeps UeexPageLegacy below. /market/ueex has no feature gate.
 */
export default function UeexPage() {
  return import.meta.env.VITE_REDESIGN === '1' ? <UeexRedesign /> : <UeexPageLegacy />;
}

function UeexPageLegacy() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [section, setSection] = useState<SectionKey>('electric_power');
  const [days, setDays] = useState(30);

  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';
  const inputBg = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const inputBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8';
  const tableHeaderBg = isDark ? 'rgba(255,255,255,0.03)' : '#F5F5F7';
  const selectBg = isDark ? '#1A1A1A' : '#fff';

  return (
    <DashboardLayout>
      <div className="px-6 py-6 max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: textMain }}>
              {t('ueex_page_title')}
            </h1>
            <p className="text-sm mt-1" style={{ color: textMuted }}>{t('ueex_page_subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm" style={{ color: textMuted }}>{t('ueex_page_window')}:</label>
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
              className="text-sm border rounded px-2 py-1.5 cursor-pointer"
              style={{ borderColor: inputBorder, background: selectBg, color: textMain }}
            >
              <option value={7}>7 {t('ueex_page_days')}</option>
              <option value={14}>14 {t('ueex_page_days')}</option>
              <option value={30}>30 {t('ueex_page_days')}</option>
              <option value={60}>60 {t('ueex_page_days')}</option>
              <option value={90}>90 {t('ueex_page_days')}</option>
              <option value={180}>180 {t('ueex_page_days')}</option>
              <option value={365}>365 {t('ueex_page_days')}</option>
            </select>
          </div>
        </div>

        <div className="flex gap-4 flex-row-reverse">
          {/* Right section nav */}
          <aside className="w-64 shrink-0">
            <div className="rounded-lg p-2 sticky top-4" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
              <div className="text-[11px] uppercase font-semibold px-2 py-1.5" style={{ color: textMuted }}>
                {t('ueex_sections_title')}
              </div>
              {SECTION_DEFS.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setSection(s.key)}
                  className="w-full text-left px-2 py-2 rounded text-sm flex items-center gap-2 cursor-pointer transition"
                  style={{
                    background: section === s.key ? (isDark ? 'color-mix(in srgb, var(--accent) 12%, transparent)' : 'var(--surface-2)') : 'transparent',
                    color: section === s.key ? 'var(--accent)' : textMuted,
                    fontWeight: section === s.key ? 500 : 400,
                  }}
                  onMouseEnter={(e) => { if (section !== s.key) e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.04)' : '#F9F9FB'; }}
                  onMouseLeave={(e) => { if (section !== s.key) e.currentTarget.style.background = 'transparent'; }}
                >
                  <i className={s.icon}></i>
                  {t(s.label_key)}
                </button>
              ))}
              <div className="mt-3 px-2 py-2" style={{ borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#F3F4F6'}` }}>
                <a
                  href="https://www.ueex.com.ua/exchange-quotations/electric-power/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[11px] hover:underline cursor-pointer"
                  style={{ color: textMuted }}
                >
                  {t('ueex_external_link')} ↗
                </a>
              </div>
            </div>
          </aside>

          {/* Content panel */}
          <div className="flex-1 min-w-0">
            {section === 'electric_power' && <ElectricPowerSection days={days} t={t} />}
            {section === 'bilateral_contracts' && <BilateralContractsSection days={days} t={t} />}
            {section === 'solar_go' && <SolarGoSection days={days} t={t} />}
            {section === 'renewable_go' && <RenewableGoSection days={days} t={t} />}
            {section === 'auctions' && <AuctionsSection days={days} t={t} />}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

// =============================================================================
// Section: Electric Power (main page like UEEX chart1)
// =============================================================================
function ElectricPowerSection({ days, t }: { days: number; t: (k: string) => string }) {
  const { isDark } = useTheme();
  const [data, setData] = useState<ElectricityTimeseriesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api.get<ElectricityTimeseriesResponse>(`/market/ueex/electricity-timeseries?days=${days}`)
      .then((r) => { if (!cancelled) setData(r); })
      .catch((e) => { if (!cancelled) setError(e?.message ?? 'load failed'); });
    return () => { cancelled = true; };
  }, [days]);

  if (error) return <ErrorBox msg={error} />;
  if (!data) return <LoadingBox t={t} />;

  const baseStats = data.stats?.base;
  const blockStats = data.stats?.block;
  const peakStats = data.stats?.peak;

  return (
    <div className="flex flex-col gap-4">
      {/* KPI strip */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <KpiCard title={t('ueex_kpi_base_wavg')} value={fmtNum(baseStats?.wavgPrice, 0)} unit={t('ak_ueex_uah_mwh')} subtitle={i18n.t('ak_ueex_mwh_deals', { mwh: fmtNum(baseStats?.totalMwh, 0), n: baseStats?.positions ?? 0 })} icon="ri-flashlight-line" color="#0066FF" />
        <KpiCard title={t('ueex_kpi_block_wavg')} value={fmtNum(blockStats?.wavgPrice, 0)} unit={t('ak_ueex_uah_mwh')} subtitle={i18n.t('ak_ueex_mwh_deals', { mwh: fmtNum(blockStats?.totalMwh, 0), n: blockStats?.positions ?? 0 })} icon="ri-stack-line" color="#8B5CF6" />
        <KpiCard title={t('ueex_kpi_peak_wavg')} value={peakStats?.wavgPrice ? fmtNum(peakStats.wavgPrice, 0) : '—'} unit={t('ak_ueex_uah_mwh')} subtitle={peakStats ? i18n.t('ak_ueex_mwh', { mwh: fmtNum(peakStats.totalMwh, 0) }) : t('ueex_no_data')} icon="ri-sun-line" color="#F59E0B" />
        <KpiCard
          title={t('ueex_kpi_premium')}
          value={
            baseStats?.wavgPrice && blockStats?.wavgPrice
              ? `${(((baseStats.wavgPrice - blockStats.wavgPrice) / blockStats.wavgPrice) * 100).toFixed(1)}%`
              : '—'
          }
          unit=""
          subtitle={t('ak_ueex_base_vs_block')}
          icon="ri-line-chart-line"
          color="#10B981"
        />
      </div>

      {/* Main chart — mimics UEEX chart1 */}
      <div className="rounded-lg p-4" style={{ background: isDark ? '#1A1A1A' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8'}` }}>
        <h3 className="font-semibold text-center mb-1" style={{ color: isDark ? '#E0E0E0' : '#1A1A1A' }}>
          {t('ueex_chart_auctions_title')}
        </h3>
        <p className="text-xs text-gray-500 text-center mb-3">
          {t('ueex_chart_auctions_subtitle')}
        </p>
        <ResponsiveContainer width="100%" height={360}>
          <LineChart data={data.points} margin={{ top: 10, right: 16, bottom: 5, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
            <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11 }} />
            <YAxis
              tick={{ fontSize: 11 }}
              label={{ value: t('ak_ueex_uah_excl_vat'), angle: -90, position: 'insideLeft', fontSize: 11, offset: 10 }}
            />
            <Tooltip formatter={(v: any) => i18n.t('ak_ueex_uah', { n: fmtNum(v, 0) })} labelFormatter={fmtDate} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="base_price"    stroke="#0066FF" name="BASE (24/7)" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
            <Line type="monotone" dataKey="block_price"   stroke="#8B5CF6" name={t('ak_ueex_block')} strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
            <Line type="monotone" dataKey="peak_price"    stroke="#F59E0B" name="PEAK" strokeWidth={2} dot={{ r: 3 }} connectNulls />
            <Line type="monotone" dataKey="offpeak_price" stroke="#6B7280" name="OFFPEAK" strokeWidth={2} dot={{ r: 3 }} connectNulls />
          </LineChart>
        </ResponsiveContainer>
        <div className="text-[10px] text-gray-400 text-right mt-1">
          {t('ueex_source_note')}: epbets.ueex.com.ua
        </div>
      </div>

      {/* Volume chart */}
      <div className="rounded-lg p-4" style={{ background: isDark ? '#1A1A1A' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8'}` }}>
        <h3 className="font-semibold text-sm mb-3" style={{ color: isDark ? '#E0E0E0' : '#1A1A1A' }}>
          {t('ueex_chart_volume_title')}
        </h3>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={data.points} margin={{ top: 5, right: 16, bottom: 5, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
            <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} label={{ value: t('ak_ueex_mwh_3'), angle: -90, position: 'insideLeft', fontSize: 11, offset: 10 }} />
            <Tooltip formatter={(v: any) => i18n.t('ak_ueex_mwh_2', { n: fmtNum(v, 0) })} labelFormatter={fmtDate} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="base_volume_mwh"  stackId="a" fill="#0066FF" name="BASE" />
            <Bar dataKey="block_volume_mwh" stackId="a" fill="#8B5CF6" name={t('ak_ueex_block')} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// =============================================================================
// Section: Bilateral contracts (table — like UEEX bilateral)
// =============================================================================
function BilateralContractsSection({ days, t }: { days: number; t: (k: string) => string }) {
  const { isDark } = useTheme();
  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api.get<{ auctions: Auction[] }>(`/market/ueex/recent-auctions?days=${days}&limit=200`)
      .then((r) => {
        if (cancelled) return;
        // Filter to electricity-related auction types
        const electricity = r.auctions.filter((a) =>
          a.goods.some((g) =>
            ['Базове навантаження', 'Блочна позиція', 'Пікове навантаження', 'Позапікове навантаження',
             'Пікове навантаження неробочого дня', 'Навантаження індивідуального профілю'].includes(g)
          )
        );
        setAuctions(electricity);
      })
      .catch((e) => { if (!cancelled) setError(e?.message ?? 'load failed'); });
    return () => { cancelled = true; };
  }, [days]);

  if (error) return <ErrorBox msg={error} />;

  return (
    <div className="rounded-lg p-4" style={{ background: isDark ? '#1A1A1A' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8'}` }}>
      <h3 className="font-semibold mb-1" style={{ color: isDark ? '#E0E0E0' : '#1A1A1A' }}>
        {t('ueex_bilateral_title')}
      </h3>
      <p className="text-xs text-gray-500 mb-3">
        {t('ueex_bilateral_subtitle')} • {auctions.length} {t('ueex_records')}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b" style={{ background: isDark ? 'rgba(255,255,255,0.03)' : '#F9FAFB', borderColor: isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8' }}>
            <tr className="border-b border-gray-200 text-left">
              <th className="py-2 px-2 font-medium text-gray-600">{t('ueex_auc_num')}</th>
              <th className="py-2 px-2 font-medium text-gray-600">{t('ueex_auc_date')}</th>
              <th className="py-2 px-2 font-medium text-gray-600">{t('ueex_auc_type')}</th>
              <th className="py-2 px-2 font-medium text-gray-600 text-right">{t('ueex_auc_closed')}/{t('ueex_auc_positions')}</th>
              <th className="py-2 px-2 font-medium text-gray-600 text-right">{t('ak_ueex_mwh_3')}</th>
              <th className="py-2 px-2 font-medium text-gray-600 text-right">{t('ueex_wavg_price')}</th>
              <th className="py-2 px-2 font-medium text-gray-600 text-right">{t('ueex_revenue')}, {t('ak_ueex_uah_2')}</th>
            </tr>
          </thead>
          <tbody>
            {auctions.length === 0 && (
              <tr><td colSpan={7} className="py-6 text-center text-gray-400 text-sm">{t('ueex_no_data')}</td></tr>
            )}
            {auctions.map((a) => (
              <tr key={a.aucNum} className="border-b hover:bg-[var(--hover-bg)]" style={{ borderColor: isDark ? 'rgba(255,255,255,0.05)' : '#F3F4F6', ['--hover-bg' as string]: isDark ? 'rgba(255,255,255,0.04)' : '#F9FAFB' }}>
                <td className="py-2 px-2 font-mono text-xs">{a.aucNum}</td>
                <td className="py-2 px-2 font-mono text-xs">{fmtDate(a.aucDate)}</td>
                <td className="py-2 px-2 text-xs">{a.aucTypeName}</td>
                <td className="py-2 px-2 text-right font-mono">{a.closedCount}/{a.positionsCount}</td>
                <td className="py-2 px-2 text-right font-mono">{fmtNum(a.totalMwh, 0)}</td>
                <td className="py-2 px-2 text-right font-mono">{fmtNum(a.wavgPrice, 0)}</td>
                <td className="py-2 px-2 text-right font-mono">{fmtNum(a.totalUah, 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
// Section: Solar GO
// =============================================================================
function SolarGoSection({ days, t }: { days: number; t: (k: string) => string }) {
  const { isDark } = useTheme();
  const [data, setData] = useState<SolarGoResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api.get<SolarGoResponse>(`/market/ueex/solar-go-timeseries?days=${days}`)
      .then((r) => { if (!cancelled) setData(r); })
      .catch((e) => { if (!cancelled) setError(e?.message ?? 'load failed'); });
    return () => { cancelled = true; };
  }, [days]);

  if (error) return <ErrorBox msg={error} />;
  if (!data) return <LoadingBox t={t} />;

  const totalSell = data.points.reduce((s, p) => s + p.sellPositions, 0);
  const totalClosed = data.points.reduce((s, p) => s + p.closedWithBuyer, 0);
  const totalOffered = data.points.reduce((s, p) => s + (p.offeredMwh ?? 0), 0);
  const asks = data.points.map((p) => p.avgAskPrice).filter((v) => v !== null) as number[];
  const avgAsk = asks.length ? asks.reduce((a, b) => a + b, 0) / asks.length : null;

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-lg p-4" style={{ background: isDark ? 'rgba(255,255,255,0.03)' : '#F9FAFB', border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8'}` }}>
        <i className="ri-information-line text-yellow-600 mt-0.5"></i>
        <div className="text-sm text-yellow-800">
          <strong>{t('ueex_solar_banner_title')}.</strong> {t('ueex_solar_banner_text')}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard title={t('ueex_solar_avg_ask')} value={avgAsk ? fmtNum(avgAsk, 2) : '—'} unit={t('ak_ueex_uah_mwh')} subtitle="" icon="ri-sun-line" color="#F59E0B" />
        <KpiCard title={t('ueex_solar_sell_positions')} value={fmtNum(totalSell, 0)} unit={t('ak_ueex_lots')} subtitle="" icon="ri-shopping-cart-line" color="#0066FF" />
        <KpiCard title={t('ueex_solar_closed_deals')} value={fmtNum(totalClosed, 0)} unit={t('ak_ueex_deals')} subtitle="" icon="ri-handshake-line" color="#10B981" />
        <KpiCard title={t('ueex_solar_offered_volume')} value={fmtNum(totalOffered, 0)} unit={t('ak_ueex_mwh_3')} subtitle="" icon="ri-database-line" color="#8B5CF6" />
      </div>

      <div className="rounded-lg p-4" style={{ background: isDark ? '#1A1A1A' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8'}` }}>
        <h3 className="font-semibold text-sm mb-3" style={{ color: isDark ? '#E0E0E0' : '#1A1A1A' }}>
          {t('ueex_solar_chart_title')}
        </h3>
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={data.points}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
            <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11 }} />
            <YAxis yAxisId="left" tick={{ fontSize: 11 }} label={{ value: t('ak_ueex_uah_mwh'), angle: -90, position: 'insideLeft', fontSize: 11 }} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} label={{ value: t('ak_ueex_mwh_3'), angle: 90, position: 'insideRight', fontSize: 11 }} />
            <Tooltip labelFormatter={fmtDate} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar  yAxisId="right" dataKey="offeredMwh"   fill="#FEF3C7" name={t('ak_ueex_offered_mwh')} />
            <Line yAxisId="left"  dataKey="avgAskPrice"  stroke="#F59E0B" name="Avg Ask" strokeWidth={2} />
            <Line yAxisId="left"  dataKey="minAskPrice"  stroke="#10B981" name="Min Ask" strokeWidth={1} strokeDasharray="4 4" />
            <Line yAxisId="left"  dataKey="maxAskPrice"  stroke="#EF4444" name="Max Ask" strokeWidth={1} strokeDasharray="4 4" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// =============================================================================
// Section: All renewable GO comparison
// =============================================================================
function RenewableGoSection({ days, t }: { days: number; t: (k: string) => string }) {
  const { isDark } = useTheme();
  return (
    <div className="rounded-lg p-4" style={{ background: isDark ? '#1A1A1A' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8'}` }}>
      <h3 className="font-semibold mb-2" style={{ color: isDark ? '#E0E0E0' : '#1A1A1A' }}>
        {t('ueex_renewable_title')}
      </h3>
      <p className="text-sm text-gray-500 mb-3">{t('ueex_renewable_subtitle')}</p>
      <div className="text-xs text-gray-400 py-6 text-center bg-gray-50 rounded">
        {t('ueex_renewable_placeholder')}<br />
        <span className="text-[11px]">{t('ueex_renewable_data_source')}</span>
      </div>
    </div>
  );
}

// =============================================================================
// Section: All auctions list
// =============================================================================
function AuctionsSection({ days, t }: { days: number; t: (k: string) => string }) {
  const { isDark } = useTheme();
  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>('all');

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api.get<{ auctions: Auction[] }>(`/market/ueex/recent-auctions?days=${days}&limit=200`)
      .then((r) => { if (!cancelled) setAuctions(r.auctions); })
      .catch((e) => { if (!cancelled) setError(e?.message ?? 'load failed'); });
    return () => { cancelled = true; };
  }, [days]);

  if (error) return <ErrorBox msg={error} />;

  const allTypes = Array.from(new Set(auctions.map((a) => a.aucTypeName))).filter(Boolean);
  const filtered = typeFilter === 'all' ? auctions : auctions.filter((a) => a.aucTypeName === typeFilter);

  return (
    <div className="rounded-lg p-4" style={{ background: isDark ? '#1A1A1A' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8'}` }}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-semibold" style={{ color: isDark ? '#E0E0E0' : '#1A1A1A' }}>
          {t('ueex_all_auctions_title')} ({filtered.length})
        </h3>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="text-sm border border-gray-200 rounded px-2 py-1.5"
        >
          <option value="all">{t('ueex_all_types')}</option>
          {allTypes.map((tp) => <option key={tp} value={tp}>{tp}</option>)}
        </select>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b" style={{ background: isDark ? 'rgba(255,255,255,0.03)' : '#F9FAFB', borderColor: isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8' }}>
            <tr className="border-b border-gray-200 text-left">
              <th className="py-2 px-2 font-medium text-gray-600">{t('ueex_auc_num')}</th>
              <th className="py-2 px-2 font-medium text-gray-600">{t('ueex_auc_date')}</th>
              <th className="py-2 px-2 font-medium text-gray-600">{t('ueex_auc_type')}</th>
              <th className="py-2 px-2 font-medium text-gray-600">{t('ueex_auc_state')}</th>
              <th className="py-2 px-2 font-medium text-gray-600 text-right">{t('ueex_auc_positions')}</th>
              <th className="py-2 px-2 font-medium text-gray-600 text-right">{t('ueex_auc_closed')}</th>
              <th className="py-2 px-2 font-medium text-gray-600 text-right">{t('ak_ueex_mwh_3')}</th>
              <th className="py-2 px-2 font-medium text-gray-600 text-right">{t('ueex_wavg_price')}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="py-6 text-center text-gray-400 text-sm">{t('ueex_no_data')}</td></tr>
            )}
            {filtered.map((a) => (
              <tr key={a.aucNum} className="border-b hover:bg-[var(--hover-bg)]" style={{ borderColor: isDark ? 'rgba(255,255,255,0.05)' : '#F3F4F6', ['--hover-bg' as string]: isDark ? 'rgba(255,255,255,0.04)' : '#F9FAFB' }}>
                <td className="py-2 px-2 font-mono text-xs">{a.aucNum}</td>
                <td className="py-2 px-2 font-mono text-xs">{fmtDate(a.aucDate)}</td>
                <td className="py-2 px-2 text-xs">{a.aucTypeName}</td>
                <td className="py-2 px-2 text-xs">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                    a.aucState === 'Закрито' ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-700'
                  }`}>{a.aucState}</span>
                </td>
                <td className="py-2 px-2 text-right font-mono">{a.positionsCount}</td>
                <td className="py-2 px-2 text-right font-mono">{a.closedCount}</td>
                <td className="py-2 px-2 text-right font-mono">{fmtNum(a.totalMwh, 0)}</td>
                <td className="py-2 px-2 text-right font-mono">{fmtNum(a.wavgPrice, 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
// Common UI parts
// =============================================================================
function KpiCard({ title, value, unit, subtitle, icon, color }: {
  title: string; value: string; unit: string; subtitle: string; icon: string; color: string;
}) {
  const { isDark } = useTheme();
  return (
    <div className="rounded-lg p-4" style={{ background: isDark ? '#1A1A1A' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8'}` }}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs" style={{ color: isDark ? '#6B6B70' : '#6B6B70' }}>{title}</div>
        <i className={icon} style={{ color }}></i>
      </div>
      <div className="font-mono text-xl font-semibold" style={{ color: isDark ? '#E0E0E0' : '#1A1A1A' }}>
        {value}
        {unit && <span className="text-xs font-normal ml-1" style={{ color: isDark ? '#4A4A50' : '#9B9BA4' }}>{unit}</span>}
      </div>
      {subtitle && <div className="text-[10px] mt-1" style={{ color: isDark ? '#4A4A50' : '#9B9BA4' }}>{subtitle}</div>}
    </div>
  );
}

function LoadingBox({ t }: { t: (k: string) => string }) {
  return <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-sm text-gray-400">{t('ueex_loading')}</div>;
}

function ErrorBox({ msg }: { msg: string }) {
  return <div className="rounded p-3 text-sm bg-[color-mix(in_srgb,var(--err)_10%,transparent)] border border-[color-mix(in_srgb,var(--err)_30%,transparent)] text-[var(--err)]">{msg}</div>;
}
